import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KnowledgeBaseContext, KnowledgeBaseContextValue } from 'src/context/KnowledgeBaseContext';
import {
  getInitialTree,
  mergeWithEmptyFolders,
  setVisibilityByTypes,
  sortByDate,
  sortByName,
  sortByType,
} from 'src/utils/fileUtils';
import { getKnowledgeBaseConfig } from 'src/utils/pageStorageConfig';
import { useLoader } from 'src/provider/LoaderProvider';
import { useAppSelector } from 'src/store';
import type { TreeNode } from 'src/utils/fileUtils';

export function useKBContext(): KnowledgeBaseContextValue | null {
  return useContext(KnowledgeBaseContext);
}

export function KnowledgeBaseProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [tree, setTree] = useState(getInitialTree());
  const [activeBranch, setActiveBranch] = useState(getInitialTree());
  const [selectedItem, setSelectedItem] = useState(activeBranch);
  const [filteredValue, setFilteredValue] = useState('');
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('Knowledge Base');
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [skipRefetchFiles, setSkipRefetchFiles] = useState(false);
  const { filteredTypes } = useAppSelector((s) => s.knowledgeBaseSlices);
  const loader = useLoader();

  useEffect(() => {
    setSkipRefetchFiles(isEditPopupOpen);
  }, [isEditPopupOpen]);

  const updateActiveBranch = useCallback(
    (branch: TreeNode) => {
      loader.show();
      mergeWithEmptyFolders(branch);

      const { sortBy } = getKnowledgeBaseConfig();

      switch (sortBy) {
        case 'type':
          sortByType(branch.children);
          break;
        case 'name':
          sortByName(branch.children);
          sortByType(branch.children);
          break;
        case 'date':
          sortByDate(branch.children);
          sortByType(branch.children);
          break;
        default:
          sortByType(branch.children);
          break;
      }

      setVisibilityByTypes(branch, filteredTypes);
      setActiveBranch(branch);
      loader.hide();
    },
    [filteredTypes, loader],
  );

  const value = useMemo(
    () => ({
      tree,
      setTree,
      activeBranch,
      updateActiveBranch,
      selectedItem,
      setSelectedItem,
      filteredValue,
      setFilteredValue,
      knowledgeBaseName,
      setKnowledgeBaseName,
      isEditPopupOpen,
      setIsEditPopupOpen,
      skipRefetchFiles,
      setSkipRefetchFiles,
    }),
    [
      tree,
      activeBranch,
      updateActiveBranch,
      selectedItem,
      filteredValue,
      knowledgeBaseName,
      isEditPopupOpen,
      skipRefetchFiles,
    ],
  );

  return <KnowledgeBaseContext.Provider value={value}>{children}</KnowledgeBaseContext.Provider>;
}
